import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import { FiShield, FiSun, FiUsers } from 'react-icons/fi';

const IMAGES = {
  airports: `${process.env.PUBLIC_URL}/Images/Airports.png`,
  demo: `${process.env.PUBLIC_URL}/Images/Demo_img_1.png`,
  hospital: `${process.env.PUBLIC_URL}/Images/Robot_1.png`,
};

const LOGO_MARK = `${process.env.PUBLIC_URL}/logo63.png`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pillars = [
  {
    icon: <FiSun size={22} />,
    title: 'UV-C germicidal range',
    text: 'Short-wavelength UV-C is absorbed by microbial DNA and RNA, disrupting replication so germs cannot multiply effectively.',
  },
  {
    icon: <FiShield size={22} />,
    title: 'No-touch coverage',
    text: 'Light reaches exposed surfaces and line-of-sight areas across a room, helping sanitize where wipes may be harder to apply evenly.',
  },
  {
    icon: <FiUsers size={22} />,
    title: 'Safety first',
    text: 'UV-C can harm skin and eyes. Cycles run when people are not in the treated zone, following your site’s safety procedures.',
  },
];

const useCases = [
  {
    eyebrow: 'Transportation',
    title: 'Airports and in-flight environments',
    body:
      'From busy terminals to aircraft cabins, Sportbots help teams target high-touch zones and open areas with scheduled UV cycles—supporting a layered approach to hygiene so travelers and staff experience spaces that are cleaned and disinfected with precision from boarding to landing.',
    image: IMAGES.airports,
    alt: 'Sportbot UV disinfection robot in an airport terminal',
    imageReverse: false,
  },
  {
    eyebrow: 'Technology',
    title: 'UV disinfection in action',
    body:
      'During a run, the robot positions its UV lamps to deliver a controlled dose of germicidal light. The visible glow in marketing imagery illustrates the active cycle; your deployment follows manufacturer and site guidelines for duration, distance, and re-entry time after treatment.',
    note: 'Always follow local regulations and the product manual. UV-C is not a substitute for all infection-control measures.',
    image: IMAGES.demo,
    alt: 'UV disinfection robot demonstration with germicidal lamps active',
    imageReverse: true,
  },
  {
    eyebrow: 'Healthcare',
    title: 'Hospital rooms and clinical spaces',
    body:
      'In patient rooms and clinical areas, UV cycles can supplement terminal cleaning between cases or during off-peak hours. The robot navigates to treat beds, equipment, and surrounding surfaces while the space is clear—helping reduce bioburden on exposed surfaces as part of your facility’s infection-prevention plan.',
    image: IMAGES.hospital,
    alt: 'UV disinfection robot operating in a hospital patient room',
    imageReverse: false,
  },
];

/**
 * Sportbot Lite tutorial — UV disinfection overview with use-case imagery.
 */
export default function SportbotLiteTutorial() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ImageWithWatermark = ({ src, alt }) => (
    <Box sx={sx.imageFrame}>
      <Box component="img" src={src} alt={alt} loading="lazy" decoding="async" sx={sx.imageCover} />
      <Box sx={sx.watermarkBadge} aria-hidden>
        <Box component="img" src={LOGO_MARK} alt="" sx={sx.watermarkLogoImg} />
      </Box>
    </Box>
  );

  return (
    <Box sx={sx.page}>
      {/* Top hero */}
      <Box sx={sx.heroWrap}>
        <Box sx={sx.heroOrb} aria-hidden />
        <Box sx={sx.heroOrb2} aria-hidden />
        <Container maxWidth="lg" sx={sx.heroInner}>
          <Chip label="Sportbot Lite · UV disinfection" sx={sx.heroChip} />
          <Typography
            component="h1"
            sx={{
              ...sx.heroTitle,
              animation: `${fadeUp} 0.7s ease-out both`,
            }}
          >
            Understanding UV disinfection
          </Typography>
          <Typography
            variant="body1"
            sx={{
              ...sx.heroLead,
              animation: `${fadeUp} 0.7s ease-out 0.06s both`,
            }}
          >
            Sportbot Lite uses controlled ultraviolet (UV-C) light to reduce pathogens on surfaces and in the air in
            spaces that are unoccupied during a cycle. It supports your cleaning program with consistent, measurable
            coverage—without replacing routine manual cleaning where it is required.
          </Typography>
        </Container>
      </Box>

      {/* Use cases */}
      {useCases.map((uc, index) => (
        <Box
          key={uc.title}
          sx={{
            ...sx.section,
            bgcolor: index % 2 === 1 ? 'rgba(15, 23, 42, 0.02)' : 'transparent',
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={{ xs: 4, md: 6 }}
              alignItems="center"
              direction={{ xs: 'column', md: uc.imageReverse ? 'row-reverse' : 'row' }}
            >
              <Grid item xs={12} md={6}>
                <ImageWithWatermark src={uc.image} alt={uc.alt} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="overline" sx={sx.sectionEyebrow}>
                  {uc.eyebrow}
                </Typography>
                <Typography variant="h2" sx={sx.sectionTitle}>
                  {uc.title}
                </Typography>
                <Typography variant="body1" sx={sx.sectionBody}>
                  {uc.body}
                </Typography>
                {uc.note && (
                  <Typography variant="body2" sx={sx.sectionBodyMuted}>
                    {uc.note}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}

      {/* Pillars — above footer */}
      <Box sx={sx.pillarSection}>
        <Container maxWidth="lg">
          <Grid container spacing={2}>
            {pillars.map((item, i) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Paper
                  elevation={0}
                  sx={{
                    ...sx.pillarCard,
                    animation: `${fadeUp} 0.65s ease-out ${0.05 * (i + 1)}s both`,
                  }}
                >
                  <Box sx={sx.pillarIcon}>{item.icon}</Box>
                  <Typography variant="subtitle2" sx={sx.pillarTitle}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={sx.pillarText}>
                    {item.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer CTA */}
      <Box sx={sx.footerBand}>
        <Box sx={sx.footerGlow} aria-hidden />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" sx={sx.footerTitle}>
            Sportbot Lite & SpotlessAI Admin
          </Typography>
          <Typography variant="body1" sx={sx.footerText}>
            Use the admin portal to schedule monitoring, review robot activity, and align disinfection workflows with
            your organization’s protocols.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip label="Fleet visibility" sx={sx.footerChip} />
            <Chip label="Protocol alignment" sx={sx.footerChip} />
            <Chip label="Operational monitoring" sx={sx.footerChip} />
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

const sx = {
  page: {
    minHeight: '100vh',
    bgcolor: '#f8fafc',
  },
  heroWrap: {
    position: 'relative',
    overflow: 'hidden',
    pt: { xs: 5, md: 7 },
    pb: { xs: 6, md: 8 },
    background: 'linear-gradient(180deg, #f0f9ff 0%, #eef2ff 35%, #f8fafc 100%)',
  },
  heroOrb: {
    position: 'absolute',
    top: '-30%',
    right: '-10%',
    width: '55%',
    height: '90%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 65%)',
    filter: 'blur(50px)',
    pointerEvents: 'none',
  },
  heroOrb2: {
    position: 'absolute',
    bottom: '-25%',
    left: '-15%',
    width: '50%',
    height: '70%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(14, 165, 233, 0.18) 0%, transparent 60%)',
    filter: 'blur(45px)',
    pointerEvents: 'none',
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
  },
  heroChip: {
    mb: 2,
    fontWeight: 700,
    letterSpacing: '0.06em',
    bgcolor: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    color: '#4338ca',
  },
  heroTitle: {
    m: 0,
    mb: 2,
    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
    fontWeight: 800,
    letterSpacing: '-0.04em',
    lineHeight: 1.1,
    background: 'linear-gradient(105deg, #1d4ed8 0%, #6d28d9 45%, #be185d 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    maxWidth: '16ch',
  },
  heroLead: {
    color: '#475569',
    lineHeight: 1.8,
    maxWidth: 'min(720px, 100%)',
    fontSize: { xs: '1.05rem', md: '1.125rem' },
  },
  pillarCard: {
    p: 2.5,
    height: '100%',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.95)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
    boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 24px 48px -12px rgba(15, 23, 42, 0.08)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px -4px rgba(79, 70, 229, 0.12)',
    },
  },
  pillarIcon: {
    color: '#4f46e5',
    mb: 1.25,
    display: 'flex',
  },
  pillarTitle: {
    fontWeight: 700,
    color: '#0f172a',
    mb: 0.75,
    letterSpacing: '-0.02em',
  },
  pillarText: {
    color: '#64748b',
    lineHeight: 1.65,
    fontSize: '0.9rem',
  },
  section: {
    py: { xs: 5, md: 7 },
  },
  sectionEyebrow: {
    color: '#4f46e5',
    fontWeight: 700,
    letterSpacing: '0.14em',
    display: 'block',
    mb: 1,
    fontSize: '0.7rem',
  },
  sectionTitle: {
    m: 0,
    mb: 2,
    fontSize: { xs: '1.5rem', md: '1.85rem' },
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.03em',
    lineHeight: 1.25,
  },
  sectionBody: {
    color: '#475569',
    lineHeight: 1.85,
    fontSize: '1.02rem',
    mb: 1.5,
  },
  sectionBodyMuted: {
    color: '#64748b',
    lineHeight: 1.65,
    fontStyle: 'italic',
    fontSize: '0.9rem',
  },
  pillarSection: {
    py: { xs: 5, md: 6 },
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  },
  imageFrame: {
    position: 'relative',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.2), 0 0 0 1px rgba(255,255,255,0.6) inset',
    background: '#0f172a',
  },
  watermarkBadge: {
    position: 'absolute',
    right: { xs: 4, sm: 6, md: 8 },
    bottom: { xs: 4, sm: 6, md: 8 },
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: { xs: '2px', sm: '3px', md: '3px' },
    minWidth: { xs: 36, sm: 40, md: 44 },
    minHeight: { xs: 36, sm: 40, md: 44 },
    borderRadius: 1,
    pointerEvents: 'none',
    background: 'rgba(255, 255, 255, 0.96)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    boxShadow: '0 1px 6px rgba(0, 0, 0, 0.2)',
  },
  watermarkLogoImg: {
    width: '100%',
    maxWidth: { xs: 30, sm: 34, md: 38 },
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))',
  },
  imageCover: {
    width: '100%',
    height: 'auto',
    display: 'block',
    maxHeight: { xs: '300px', sm: '380px', md: '440px' },
    objectFit: 'cover',
    objectPosition: 'center',
  },
  footerBand: {
    position: 'relative',
    overflow: 'hidden',
    py: { xs: 6, md: 8 },
    background: 'linear-gradient(135deg, #1e3a8a 0%, #4c1d95 48%, #9d174d 100%)',
    color: '#fff',
  },
  footerGlow: {
    position: 'absolute',
    top: '-20%',
    right: '-10%',
    width: '50%',
    height: '120%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  footerTitle: {
    fontWeight: 800,
    mb: 2,
    color: '#fff',
    textAlign: 'center',
    letterSpacing: '-0.02em',
  },
  footerText: {
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 1.75,
    maxWidth: 560,
    mx: 'auto',
    textAlign: 'center',
  },
  footerChip: {
    bgcolor: 'rgba(255,255,255,0.12)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.18)',
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
};
